/**
 * A very small stand-in for the DOTween sequences the original uses.
 *
 * Only what the gameplay needs: timed tweens with an easing curve, run off a
 * single update tick, cancellable, with an optional completion callback. The
 * easing names match DOTween's so the decompiled `SetEase` calls translate
 * directly.
 */

import type { Vector3 } from 'three'

export type Easing = (t: number) => number

export const Ease: Record<string, Easing> = {
  linear: (t) => t,
  inQuad: (t) => t * t,
  outQuad: (t) => t * (2 - t),
  inOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  inCubic: (t) => t * t * t,
  outCubic: (t) => 1 - (1 - t) ** 3,
  inOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  outBack: (t) => {
    const c1 = 1.70158
    const c3 = c1 + 1
    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2
  },
  outElastic: (t) => {
    if (t === 0 || t === 1) return t
    const c4 = (2 * Math.PI) / 3
    return 2 ** (-10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1
  },
}

/**
 * `ShortcutExtensions.DOJump(endValue, jumpPower, numJumps, duration)`, written
 * out as a closed form.
 *
 * DOTween builds it as a sequence of three tweens plus an `OnUpdate` hook:
 *
 *     s.Append(To(x -> endValue.x, duration).SetEase(Ease.Linear))
 *      .Join (To(z -> endValue.z, duration).SetEase(Ease.Linear))
 *      .Join (To(+jumpPower on Y, duration / (numJumps * 2))
 *               .SetEase(Ease.OutQuad).SetRelative()
 *               .SetLoops(numJumps * 2, LoopType.Yoyo))
 *     s.OnUpdate(() => pos.y += DOVirtual.EasedValue(0, endY - startY, t, Ease.OutQuad))
 *
 * Three consequences worth spelling out, because the port used to guess all
 * three:
 *
 *  - **X and Z are linear**, explicitly, inside DOTween. The `SetEase` the call
 *    site applies lands on the `Sequence`, and a `Sequence` ignores ease — so
 *    unusually for this codebase the erased `SetEase` argument does not matter
 *    here (contrast `DORotate`, which is a `Tweener`).
 *  - **Y approaches its target on OutQuad**, not linearly.
 *  - **the hop is a plain parabola.** The yoyo of `OutQuad` works out to
 *    `1 - (1 - 2t)^2` on the way up and `1 - (2t - 1)^2` on the way down, and
 *    both are `4t(1 - t)` — so one hop is exactly `4 * jumpPower * t * (1 - t)`,
 *    peaking at `jumpPower` halfway. Not `sin(pi * t)`, which the port used
 *    before and which is 6% low at the quarter points.
 *
 * @param t normalised 0..1 progress
 * @param numJumps DOTween's `numJumps`; every call site in this game passes 1
 */
export function doJump(
  from: Vector3,
  to: Vector3,
  jumpPower: number,
  t: number,
  out: Vector3,
  numJumps = 1,
): Vector3 {
  const k = Math.max(0, Math.min(1, t))
  out.x = from.x + (to.x - from.x) * k
  out.z = from.z + (to.z - from.z) * k
  out.y = from.y + (to.y - from.y) * Ease.outQuad(k)
  // One hop is 4Pt(1-t) over its own slice of the duration; `numJumps` of them
  // divide the run into equal slices, each a fresh parabola.
  const n = Math.max(1, Math.trunc(numJumps))
  const local = (k * n) % 1
  const hop = k >= 1 ? 0 : local
  out.y += 4 * jumpPower * hop * (1 - hop)
  return out
}

export interface TweenHandle {
  /** Stop without firing `onComplete`. */
  kill(): void
  readonly done: boolean
}

interface ActiveTween {
  elapsed: number
  duration: number
  delay: number
  ease: Easing
  onUpdate(t: number): void
  onComplete?: () => void
  killed: boolean
  done: boolean
}

export class TweenRunner {
  private tweens: ActiveTween[] = []

  to(options: {
    duration: number
    delay?: number
    ease?: Easing
    onUpdate(t: number): void
    onComplete?: () => void
  }): TweenHandle {
    const tween: ActiveTween = {
      elapsed: 0,
      duration: Math.max(options.duration, 1e-6),
      delay: options.delay ?? 0,
      ease: options.ease ?? Ease.linear,
      onUpdate: options.onUpdate,
      onComplete: options.onComplete,
      killed: false,
      done: false,
    }
    this.tweens.push(tween)
    return {
      kill: () => {
        tween.killed = true
      },
      get done() {
        return tween.done
      },
    }
  }

  /** Run a callback after `delay` seconds. DOTween's `DOVirtual.DelayedCall`. */
  delay(seconds: number, onComplete: () => void): TweenHandle {
    return this.to({ duration: 1e-6, delay: seconds, onUpdate: () => {}, onComplete })
  }

  update(dt: number): void {
    if (this.tweens.length === 0) return
    // Iterate a snapshot: completion callbacks routinely start new tweens.
    const running = this.tweens
    this.tweens = []

    for (const tween of running) {
      if (tween.killed) continue

      let remaining = dt
      if (tween.delay > 0) {
        const used = Math.min(tween.delay, remaining)
        tween.delay -= used
        remaining -= used
        if (tween.delay > 0) {
          this.tweens.push(tween)
          continue
        }
      }

      tween.elapsed += remaining
      const t = Math.min(1, tween.elapsed / tween.duration)
      tween.onUpdate(tween.ease(t))

      if (t >= 1) {
        tween.done = true
        tween.onComplete?.()
      } else {
        this.tweens.push(tween)
      }
    }
  }

  killAll(): void {
    for (const tween of this.tweens) tween.killed = true
    this.tweens = []
  }

  get activeCount(): number {
    return this.tweens.length
  }
}
